<template>
  <b-section-loader :loading="initLoading">
    <div>
      <div
        class="mb-4 d-flex align-center justify-space-between flex-wrap ga-2">
        <v-btn border color="white" @click="router.back()">
          <v-icon icon="mdi-chevron-left"></v-icon>
          <span class="font-weight-medium">Назад</span>
        </v-btn>

        <div class="d-flex align-center ga-2 flex-wrap">
          <b-action-menu :actions="actionMenu">
            <template #activator="{ props }">
              <v-btn border color="white" v-bind="props">
                <v-icon class="mr-1" icon="mdi-dots-horizontal"></v-icon>
                <span class="font-weight-medium">Дії</span>
                <v-icon class="ml-1" icon="mdi-chevron-down"></v-icon>
              </v-btn>
            </template>
          </b-action-menu>

          <template v-if="showReviewActions">
            <v-btn
              :loading="$loading.isLoading('finalCompleteHandler')"
              color="blue-darken-3"
              @click="() => actions.taskFinalComplete(task)">
              <v-icon class="mr-1" icon="mdi-check"></v-icon>
              <span class="font-weight-medium">Прийняти</span>
            </v-btn>

            <v-btn
              :loading="$loading.isLoading('revisionTaskHandler')"
              color="error"
              @click="() => actions.taskRevision(task)">
              <v-icon class="mr-1" color="white" icon="mdi-keyboard-return" />
              <span class="font-weight-medium text-white">Повернути</span>
            </v-btn>
          </template>

          <v-btn
            v-if="!actions.disabled(task).takeToWork.value"
            :loading="$loading.isLoading('taskTakeToWork')"
            color="blue-darken-3"
            @click="() => actions.taskTakeToWork(task)">
            <v-icon class="mr-1" color="white">mdi-play</v-icon>
            <span class="font-weight-medium text-white"> Взяти в роботу </span>
          </v-btn>

          <v-btn
            v-if="!actions.disabled(task).complete.value"
            :loading="$loading.isLoading('taskComplete')"
            color="green-darken-2"
            @click="() => actions.taskComplete(task)">
            <v-icon class="mr-1" color="white">mdi-check</v-icon>
            <span class="font-weight-medium text-white"> Завершити </span>
          </v-btn>

          <v-btn
            :disabled="disabled || (!task.$hasChanges && !assigneeDraftChanged)"
            border
            @click="submitTask">
            <v-icon class="mr-1" icon="mdi-check"></v-icon>
            <span class="font-weight-medium">Зберегти</span>
          </v-btn>
        </div>
      </div>

      <v-row>
        <v-col cols="12" lg="8">
          <v-card border rounded="lg">
            <task-header v-model="task" :disabled="disabled" />

            <v-card-text class="pt-0">
              <b-input
                v-model="task.topic"
                :error-messages="getErrorMessages(v$.topic)"
                :readonly="disabled"
                class="mb-3"
                hide-details
                label="Назва"
                placeholder="Введіть текст"
                @blur="v$.topic.$touch()" />
              <b-textarea
                v-model="task.description"
                :error-messages="getErrorMessages(v$.description)"
                :readonly="disabled"
                hide-details
                label="Опис"
                placeholder="Введіть текст"
                rows="3"
                @blur="v$.description.$touch()" />

              <task-details v-model="task" :disabled="disabled" />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col class="pl-0" cols="12" lg="4">
          <Sidebar
            v-model:task="task"
            :disabled="disabled"
            :v$="v$"
            @update:assignee-draft="assigneeDraft = $event" />
        </v-col>
      </v-row>
    </div>
  </b-section-loader>
</template>

<script lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useEditTask } from '@/pages/task/helpers/editTask'
import { getErrorMessages } from '@/utils/helpers'
import { socket, taskNotification } from '@/socket'
import { Task } from '@/utils/types/task'
import { omit } from 'lodash'
import { CachedObjectProxy } from 'best-modules/utils/cachedObject'
import { BActionMenu, BSectionLoader, BTextarea } from 'best-modules/components'
import Sidebar from '@/pages/task/single/components/sidebar/Sidebar.vue'
import TaskHeader from '@/pages/task/single/components/TaskHeader.vue'
import TaskDetails from '@/pages/task/single/components/TaskDetails.vue'
import { initComments } from '@/components/comments/v2'
import { ENTITY_TYPE } from '@/utils/constants'
import { useTaskActions } from '@/pages/task/helpers/taskActions'

export default {
  components: {
    TaskDetails,
    TaskHeader,
    Sidebar,
    BSectionLoader,
    BTextarea,
    BActionMenu,
  },
  methods: {
    getErrorMessages,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const taskId = computed(() => Number(route.params.id))
    const initLoading = ref(true)
    const assigneeDraft = ref({
      executorId: null,
      controlUserIds: [],
      observerIds: [],
    })
    const useTask = useEditTask()
    const actions = useTaskActions()
    const { task } = useTask as { task: CachedObjectProxy<Task> }

    initComments({ entityId: taskId.value, entityTypeId: ENTITY_TYPE.TASK })

    Promise.all([
      'getTask' in useTask ? useTask.getTask() : Promise.resolve(),
    ]).then(() => {
      socket.on(taskNotification, res => {
        if (res.id === task.id) {
          task.$set(res)
        }
      })
      initLoading.value = false
    })

    const actionMenu = computed(() => {
      const items = actions.taskActions(task)

      if (actions.disabled(task).complete.value) {
        return items
      }

      return [
        {
          title: 'Завершити завдання',
          icon: 'mdi-check',
          iconProps: {
            color: 'success',
          },
          disabled: false,
          action: () => actions.taskComplete(task),
        },
        ...items,
      ]
    })

    const showReviewActions = computed(() => {
      return (
        !actions.disabled(task).finalComplete.value ||
        !actions.disabled(task).revision.value
      )
    })

    const sameIds = (a = [], b = []) => {
      return a.length === b.length && a.every(id => b.includes(id))
    }

    const currentControlUserIds = computed(() => {
      return task.controlUsers?.map(item => item.userId) || []
    })

    const currentObserverIds = computed(() => {
      return task.observers?.map(item => item.userId) || []
    })

    const assigneeDraftChanged = computed(() => {
      return (
        assigneeDraft.value.executorId !== task.executorId ||
        !sameIds(
          assigneeDraft.value.controlUserIds,
          currentControlUserIds.value
        ) ||
        !sameIds(assigneeDraft.value.observerIds, currentObserverIds.value)
      )
    })

    const submitTask = async () => {
      await useTask.submit({
        executorId: assigneeDraft.value.executorId,
        controlUserIds: assigneeDraft.value.controlUserIds,
        observerIds: assigneeDraft.value.observerIds,
      })
      await useTask.getTask()
    }

    return {
      assigneeDraft,
      assigneeDraftChanged,
      initLoading,
      task,
      actions,
      actionMenu,
      router,
      showReviewActions,
      submitTask,
      ...omit(useTask, 'task'),
    }
  },
}
</script>
